import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { demoStreams } from '@/services/demoData';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Filter, Grid3X3, LayoutGrid, Search, SlidersHorizontal, Play, Lock, User, Zap, TrendingUp, Clock, Users, ChevronRight } from 'lucide-react';

const LiveStreams: React.FC = () => {
  const [view, setView] = useState<'grid' | 'list'>('grid');
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold">Live Streams</h1>
          <p className="text-muted-foreground">
            Discover and watch live streams from creators around the world
          </p>
        </div>
        <div className="mt-4 md:mt-0">
          <Button asChild>
            <Link to="/create-stream">Start Streaming</Link>
          </Button>
        </div>
      </div>
      
      <div className="flex flex-col md:flex-row gap-6">
        {/* Filters Sidebar */}
        <div className="w-full md:w-64 flex-shrink-0">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium">Filters</h3>
                <Button variant="ghost" size="sm" className="h-8 px-2">
                  <Filter className="h-4 w-4 mr-1" />
                  Clear
                </Button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium mb-2">Status</h4>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input type="checkbox" className="mr-2" defaultChecked />
                      <span className="text-sm">Live Now</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="mr-2" />
                      <span className="text-sm">Upcoming</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="mr-2" />
                      <span className="text-sm">Past Streams</span>
                    </label>
                  </div>
                </div>
                
                <div className="pt-2 border-t">
                  <h4 className="text-sm font-medium mb-2">Categories</h4>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input type="checkbox" className="mr-2" />
                      <span className="text-sm">Gaming</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="mr-2" />
                      <span className="text-sm">Music</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="mr-2" />
                      <span className="text-sm">Art</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="mr-2" />
                      <span className="text-sm">Talk Shows</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="mr-2" />
                      <span className="text-sm">Education</span>
                    </label>
                  </div>
                </div>
                
                <div className="pt-2 border-t">
                  <h4 className="text-sm font-medium mb-2">Access</h4>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input type="checkbox" className="mr-2" />
                      <span className="text-sm">Token-Gated</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="mr-2" />
                      <span className="text-sm">Free Access</span>
                    </label>
                  </div>
                </div>
              </div>
            </CardContent>
            <CardFooter className="px-4 py-3 border-t">
              <Button className="w-full">
                <SlidersHorizontal className="h-4 w-4 mr-2" />
                Apply Filters
              </Button>
            </CardFooter>
          </Card>
        </div>
        
        {/* Main Content */}
        <div className="flex-1">
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input 
                  type="text" 
                  placeholder="Search streams..." 
                  className="w-full pl-10 pr-4 py-2 border rounded-md"
                />
              </div>
              <div className="flex items-center">
                <span className="text-sm text-muted-foreground mr-2">Sort by:</span>
                <select className="p-2 border rounded-md">
                  <option>Viewers: High to Low</option>
                  <option>Newest First</option>
                  <option>Oldest First</option>
                </select>
              </div>
              <div className="flex items-center border rounded-md overflow-hidden">
                <button 
                  className={`p-2 ${view === 'grid' ? 'bg-muted' : ''}`}
                  onClick={() => setView('grid')}
                >
                  <Grid3X3 className="h-4 w-4" />
                </button>
                <button 
                  className={`p-2 ${view === 'list' ? 'bg-muted' : ''}`}
                  onClick={() => setView('list')}
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
          
          <Tabs defaultValue="all">
            <TabsList className="mb-6">
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="gaming">Gaming</TabsTrigger>
              <TabsTrigger value="music">Music</TabsTrigger>
              <TabsTrigger value="art">Art</TabsTrigger>
              <TabsTrigger value="talk">Talk Shows</TabsTrigger>
            </TabsList>
            
            <TabsContent value="all">
              <div className={`grid ${view === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'} gap-6`}>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
                  <StreamCard key={i} id={i} view={view} />
                ))}
              </div>
            </TabsContent>
            
            <TabsContent value="gaming">
              <div className={`grid ${view === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'} gap-6`}>
                {[1, 4, 7].map((i) => (
                  <StreamCard key={i} id={i} view={view} />
                ))}
              </div>
            </TabsContent>
            
            <TabsContent value="music">
              <div className={`grid ${view === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'} gap-6`}>
                {[2, 5, 8].map((i) => (
                  <StreamCard key={i} id={i} view={view} />
                ))}
              </div>
            </TabsContent>
            
            <TabsContent value="art">
              <div className={`grid ${view === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'} gap-6`}>
                {[3, 6, 9].map((i) => (
                  <StreamCard key={i} id={i} view={view} />
                ))}
              </div>
            </TabsContent>
            
            <TabsContent value="talk">
              <div className={`grid ${view === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'} gap-6`}>
                {[1, 3, 5, 7, 9].map((i) => (
                  <StreamCard key={i} id={i} view={view} />
                ))}
              </div>
            </TabsContent>
          </Tabs>
          
          <div className="mt-8 flex justify-center">
            <Button variant="outline">Load More</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface StreamCardProps {
  id: number;
  view: 'grid' | 'list';
}

const StreamCard: React.FC<StreamCardProps> = ({ id, view }) => {
  const isTokenGated = id % 3 === 0;
  const viewerCount = id * 42;
  
  return (
    <Card className={`overflow-hidden ${view === 'list' ? 'flex' : ''}`}>
      <div className={`${view === 'grid' ? 'aspect-video' : 'w-64 h-36'} bg-muted relative`}>
        <img 
          src={`https://picsum.photos/seed/stream${id}/640/360`}
          alt={`Stream ${id}`} 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <Button size="icon" variant="secondary" className="h-12 w-12 rounded-full opacity-90">
            <Play className="h-6 w-6" />
          </Button>
        </div>
        <div className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
          LIVE
        </div>
        {isTokenGated && (
          <div className="absolute top-2 right-2 bg-amber-500 text-white text-xs px-2 py-1 rounded-full flex items-center">
            <Lock className="h-3 w-3 mr-1" />
            GATED
          </div>
        )}
        <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full">
          {viewerCount} viewers
        </div>
      </div>
      <div className={`${view === 'list' ? 'flex-1' : ''}`}>
        <CardContent className="p-4">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="font-medium">{id % 2 === 0 ? 'Gaming Stream' : 'Creative Session'} #{id}</h3>
              <div className="flex items-center mt-1">
                <User className="h-3 w-3 mr-1 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Creator{id}</p>
              </div>
            </div>
          </div>
          {view === 'list' && (
            <p className="text-sm text-muted-foreground mt-2">
              {id % 2 === 0 
                ? 'Join this gaming session with live commentary and gameplay tips.' 
                : 'Watch this creative process unfold in real-time with audience interaction.'}
            </p>
          )}
        </CardContent>
        <CardFooter className="px-4 py-3 border-t">
          <Button className="w-full" asChild>
            <Link to={`/stream/${id}`}>
              Watch Stream
            </Link>
          </Button>
        </CardFooter>
      </div>
    </Card>
  );
};

export default LiveStreams;